// We discriminate the `node` prop before applying the remaining props to the
// resulting tag. Therefore, `node` is always defined, but never used.

import {
  Barbell,
  Bell,
  BookmarkSimple,
  Books,
  Broom,
  Bug,
  Buildings,
  Calendar,
  Camera,
  ChartBar,
  ChartLineDown,
  ChartLineUp,
  ChatCircle,
  ChatText,
  CheckCircle,
  Circle,
  ClipboardText,
  Clock,
  Confetti,
  Crane,
  Eye,
  FileText,
  FlagCheckered,
  Flask,
  FloppyDisk,
  Folder,
  FolderOpen,
  Folders,
  GearSix,
  Globe,
  HandsClapping,
  HandsPraying,
  HandWaving,
  Handshake,
  Hash,
  Hourglass,
  Image,
  Key,
  Lightbulb,
  Link,
  Lock,
  LockKey,
  MagnifyingGlass,
  MapPin,
  Megaphone,
  Microscope,
  Note,
  Package,
  Palette,
  Paperclip,
  PencilLine,
  Prohibit,
  PuzzlePiece,
  PushPin,
  Question,
  Robot,
  RocketLaunch,
  Ruler,
  Scales,
  Sparkle,
  Star,
  Tag,
  Target,
  ThumbsDown,
  ThumbsUp,
  Ticket,
  Timer,
  Toolbox,
  TrashSimple,
  Warning,
  Wrench,
  XCircle,
  Fire,
  House,
} from "@phosphor-icons/react";
import { Heading, Text } from "@radix-ui/themes";
import cx from "classnames";
import React from "react";
import { type FC, type PropsWithChildren } from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";

import rehypeColorChip from "../plugins/rehype-color-chip";
import rehypeIconizeEmoji, {
  EMOJI_ICON_TAG,
  type EmojiConcept,
} from "../plugins/rehype-iconize-emoji";
import rehypeSuperscript from "../plugins/rehype-superscript";

const headingClass = "mb-2 mt-4 first:mt-0";
const tableWrapperClass =
  "my-3 overflow-x-auto rounded-lg border border-puntt-neutral-gray-6";

// Sentinel uses Unicode private-use-area characters (U+E000, U+E001)
// that have no legitimate meaning in user-authored text, so a real
// input cannot collide with the placeholder during code-fence extraction.
const CODE_FENCE_OPEN = "";
const CODE_FENCE_CLOSE = "";
const CODE_FENCE_PLACEHOLDER = /(\d+)/g;

/**
 * Normalize the raw markdown string before rendering.
 *
 * Collapses excessive newlines and merges paragraphs that were
 * artificially split between sentence fragments. The LLM backing
 * ask-puntt periodically emits `\n\n` between a word and the comma,
 * period, or em-dash that should follow it, producing a fragmented
 * stack of one-word paragraphs at render time. This helper stitches
 * those orphans back into their parent paragraph.
 *
 * Fenced code blocks are extracted first so the merging rules never
 * touch lines like `.gitignore` or `.env` inside ```...``` regions.
 */
function normalizeNewlines(s: string): string {
  // Extract fenced code blocks so the merging rules never touch them.
  const codes: string[] = [];
  const protectedInput = s.replace(/```[\s\S]*?```/g, (m) => {
    codes.push(m);
    return `${CODE_FENCE_OPEN}${codes.length - 1}${CODE_FENCE_CLOSE}`;
  });

  let out = protectedInput.replace(/\n{3,}/g, "\n\n");
  // Merge paragraphs starting with sentence-final closing punctuation
  // (`,` `.` `;` `:` `!` `?` `)` `]`). The punctuation belongs flush
  // against the word that ended the previous paragraph.
  out = out.replace(/\n\n([.,;:!?)\]])/g, "$1");
  // Narrow merge for orphan quotes: only when the quote is followed
  // by sentence-final punctuation or a lowercase letter (the orphan
  // continuation case). Legitimate paragraphs that start with a real
  // quote like `"This is a quote..."` are preserved.
  out = out.replace(/\n\n(["'])(\s*[.,;:!?a-z])/g, "$1$2");
  // Merge paragraphs that begin with an em-dash or en-dash. Reassembly
  // uses single-space separators so dash typography reads correctly.
  out = out.replace(/\n\n([—–])\s/g, " $1 ");

  return out.replace(CODE_FENCE_PLACEHOLDER, (_, i) => codes[Number(i)]);
}

function isWhitespaceOnly(children: React.ReactNode): boolean {
  if (children == null) return true;
  const s =
    typeof children === "string"
      ? children
      : Array.isArray(children)
        ? children.map((c) => (typeof c === "string" ? c : "")).join("")
        : String(children);
  return /^\s*$/.test(s);
}

/**
 * Body-text size for paragraphs, list items, inline code, and list markers.
 * Defaults to "3" (16px) to preserve every existing consumer; the Ask Puntt
 * chat opts into "2" (14px) so message body matches the rest of the
 * conversation (user bubbles, tool cards, meta) instead of running large.
 */
type MarkdownBodySize = "1" | "2" | "3";

const LIST_TEXT_CLASS: Record<MarkdownBodySize, string> = {
  "1": "text-xs",
  "2": "text-sm",
  "3": "text-base",
};

/**
 * Maps each emoji concept (resolved by `rehype-iconize-emoji`) to a filled
 * icon-library icon plus a brand color token and an accessible label. Colors
 * are applied as Tailwind text-color classes that the Phosphor icon inherits
 * via `currentColor`, drawn from the app's existing token families
 * (`text-puntt-red-9`, `text-puntt-gold-9`, `text-puntt-blue-9`,
 * `text-puntt-pink-9`, `text-puntt-cool-gray-9`, `text-malachite-500`,
 * `text-puntt-neutral-gray-9` — see `AppSidebar` review-state presentation).
 *
 * This is the single source of truth for concept -> {icon, color, label}.
 * Adding a new emoji that reuses a concept needs no change here; only a
 * brand-new concept adds a row. `neutral` is the last-resort fallback and the
 * only grey entry — every common object/status emoji resolves to its own
 * vibe-matched, colored icon.
 */
const EMOJI_ICON_PRESENTATION: Record<
  EmojiConcept,
  { Icon: typeof Circle; colorClass: string; label: string }
> = {
  // --- Status. ---
  alert: { Icon: Circle, colorClass: "text-puntt-red-9", label: "alert" },
  ok: { Icon: Circle, colorClass: "text-malachite-500", label: "ok" },
  caution: { Icon: Circle, colorClass: "text-puntt-gold-9", label: "caution" },
  info: { Icon: Circle, colorClass: "text-puntt-blue-9", label: "info" },
  warning: { Icon: Warning, colorClass: "text-puntt-gold-9", label: "warning" },
  approved: {
    Icon: CheckCircle,
    colorClass: "text-malachite-500",
    label: "approved",
  },
  error: { Icon: XCircle, colorClass: "text-puntt-red-9", label: "error" },
  blocked: { Icon: Prohibit, colorClass: "text-puntt-red-9", label: "blocked" },
  question: {
    Icon: Question,
    colorClass: "text-puntt-blue-9",
    label: "question",
  },
  neutral: {
    Icon: Circle,
    colorClass: "text-puntt-neutral-gray-9",
    label: "neutral",
  },
  // --- Gestures / people. ---
  wave: { Icon: HandWaving, colorClass: "text-puntt-gold-9", label: "wave" },
  applause: {
    Icon: HandsClapping,
    colorClass: "text-puntt-gold-9",
    label: "applause",
  },
  thumbsUp: {
    Icon: ThumbsUp,
    colorClass: "text-malachite-500",
    label: "thumbs up",
  },
  thumbsDown: {
    Icon: ThumbsDown,
    colorClass: "text-puntt-red-9",
    label: "thumbs down",
  },
  thanks: {
    Icon: HandsPraying,
    colorClass: "text-puntt-gold-9",
    label: "thanks",
  },
  strength: {
    Icon: Barbell,
    colorClass: "text-puntt-blue-9",
    label: "strength",
  },
  handshake: {
    Icon: Handshake,
    colorClass: "text-malachite-500",
    label: "handshake",
  },
  writing: {
    Icon: PencilLine,
    colorClass: "text-puntt-blue-9",
    label: "writing",
  },
  eyes: { Icon: Eye, colorClass: "text-puntt-blue-9", label: "eyes" },
  // --- Objects / tools. ---
  palette: { Icon: Palette, colorClass: "text-puntt-pink-9", label: "palette" },
  clipboard: {
    Icon: ClipboardText,
    colorClass: "text-puntt-blue-9",
    label: "clipboard",
  },
  folder: { Icon: Folder, colorClass: "text-puntt-gold-9", label: "folder" },
  folderOpen: {
    Icon: FolderOpen,
    colorClass: "text-puntt-gold-9",
    label: "open folder",
  },
  files: {
    Icon: Folders,
    colorClass: "text-puntt-cool-gray-9",
    label: "files",
  },
  document: {
    Icon: FileText,
    colorClass: "text-puntt-blue-9",
    label: "document",
  },
  note: { Icon: Note, colorClass: "text-puntt-blue-9", label: "note" },
  search: {
    Icon: MagnifyingGlass,
    colorClass: "text-puntt-blue-9",
    label: "search",
  },
  link: { Icon: Link, colorClass: "text-puntt-blue-9", label: "link" },
  attachment: {
    Icon: Paperclip,
    colorClass: "text-puntt-cool-gray-9",
    label: "attachment",
  },
  pin: { Icon: PushPin, colorClass: "text-puntt-red-9", label: "pin" },
  barChart: {
    Icon: ChartBar,
    colorClass: "text-malachite-500",
    label: "bar chart",
  },
  chartUp: {
    Icon: ChartLineUp,
    colorClass: "text-malachite-500",
    label: "upward trend",
  },
  chartDown: {
    Icon: ChartLineDown,
    colorClass: "text-puntt-red-9",
    label: "downward trend",
  },
  image: { Icon: Image, colorClass: "text-puntt-blue-9", label: "image" },
  tag: { Icon: Tag, colorClass: "text-puntt-gold-9", label: "tag" },
  settings: {
    Icon: GearSix,
    colorClass: "text-puntt-cool-gray-9",
    label: "settings",
  },
  tools: {
    Icon: Toolbox,
    colorClass: "text-puntt-cool-gray-9",
    label: "tools",
  },
  wrench: {
    Icon: Wrench,
    colorClass: "text-puntt-cool-gray-9",
    label: "wrench",
  },
  key: { Icon: Key, colorClass: "text-puntt-gold-9", label: "key" },
  puzzle: {
    Icon: PuzzlePiece,
    colorClass: "text-puntt-pink-9",
    label: "puzzle",
  },
  package: { Icon: Package, colorClass: "text-puntt-gold-9", label: "package" },
  rocket: {
    Icon: RocketLaunch,
    colorClass: "text-puntt-blue-9",
    label: "rocket",
  },
  idea: { Icon: Lightbulb, colorClass: "text-puntt-gold-9", label: "idea" },
  notification: {
    Icon: Bell,
    colorClass: "text-puntt-gold-9",
    label: "notification",
  },
  clock: { Icon: Clock, colorClass: "text-puntt-cool-gray-9", label: "time" },
  timer: { Icon: Timer, colorClass: "text-puntt-cool-gray-9", label: "timer" },
  hourglass: {
    Icon: Hourglass,
    colorClass: "text-puntt-cool-gray-9",
    label: "waiting",
  },
  calendar: {
    Icon: Calendar,
    colorClass: "text-puntt-blue-9",
    label: "calendar",
  },
  target: { Icon: Target, colorClass: "text-puntt-red-9", label: "target" },
  finish: {
    Icon: FlagCheckered,
    colorClass: "text-puntt-cool-gray-9",
    label: "finish",
  },
  save: { Icon: FloppyDisk, colorClass: "text-puntt-blue-9", label: "save" },
  cleanup: {
    Icon: Broom,
    colorClass: "text-puntt-cool-gray-9",
    label: "cleanup",
  },
  experiment: {
    Icon: Flask,
    colorClass: "text-puntt-pink-9",
    label: "experiment",
  },
  microscope: {
    Icon: Microscope,
    colorClass: "text-puntt-blue-9",
    label: "analysis",
  },
  web: { Icon: Globe, colorClass: "text-puntt-blue-9", label: "web" },
  legal: { Icon: Scales, colorClass: "text-puntt-gold-9", label: "legal" },
  delete: {
    Icon: TrashSimple,
    colorClass: "text-puntt-red-9",
    label: "delete",
  },
  location: { Icon: MapPin, colorClass: "text-puntt-red-9", label: "location" },
  comment: {
    Icon: ChatCircle,
    colorClass: "text-puntt-blue-9",
    label: "comment",
  },
  thought: {
    Icon: ChatText,
    colorClass: "text-puntt-cool-gray-9",
    label: "thought",
  },
  bot: { Icon: Robot, colorClass: "text-puntt-blue-9", label: "bot" },
  bug: { Icon: Bug, colorClass: "text-puntt-red-9", label: "bug" },
  company: {
    Icon: Buildings,
    colorClass: "text-puntt-cool-gray-9",
    label: "company",
  },
  home: { Icon: House, colorClass: "text-puntt-blue-9", label: "home" },
  construction: {
    Icon: Crane,
    colorClass: "text-puntt-gold-9",
    label: "construction",
  },
  camera: {
    Icon: Camera,
    colorClass: "text-puntt-cool-gray-9",
    label: "camera",
  },
  measure: {
    Icon: Ruler,
    colorClass: "text-puntt-cool-gray-9",
    label: "measure",
  },
  books: { Icon: Books, colorClass: "text-puntt-gold-9", label: "books" },
  bookmarks: {
    Icon: BookmarkSimple,
    colorClass: "text-puntt-gold-9",
    label: "bookmarks",
  },
  locked: { Icon: Lock, colorClass: "text-puntt-gold-9", label: "locked" },
  secure: { Icon: LockKey, colorClass: "text-puntt-gold-9", label: "secure" },
  ticket: { Icon: Ticket, colorClass: "text-puntt-blue-9", label: "ticket" },
  numbers: { Icon: Hash, colorClass: "text-puntt-blue-9", label: "numbers" },
  announcement: {
    Icon: Megaphone,
    colorClass: "text-puntt-gold-9",
    label: "announcement",
  },
  // --- Symbols. ---
  star: { Icon: Star, colorClass: "text-puntt-gold-9", label: "star" },
  sparkle: { Icon: Sparkle, colorClass: "text-puntt-gold-9", label: "sparkle" },
  fire: { Icon: Fire, colorClass: "text-puntt-red-9", label: "hot" },
  celebration: {
    Icon: Confetti,
    colorClass: "text-puntt-pink-9",
    label: "celebration",
  },
};

/**
 * Inline icon substituted for a chat emoji. The wrapper carries the
 * `data-testid` and the accessible name (`role="img"` + `aria-label`) because
 * it stands in for a standalone emoji; the icon itself is `aria-hidden` so the
 * concept is announced exactly once. Sized at `1em` so it flows with the text.
 */
function EmojiStatusIcon({ node }: { node?: any }) {
  const name = node?.properties?.emojiName as EmojiConcept | undefined;
  const preset = name ? EMOJI_ICON_PRESENTATION[name] : undefined;
  if (!preset) return null;
  const { Icon, colorClass, label } = preset;
  return (
    <span
      data-testid={`markdown-emoji-icon-${name}`}
      role="img"
      aria-label={label}
      className={cx("inline-flex items-center align-text-bottom", colorClass)}
    >
      <Icon size="1em" weight="fill" aria-hidden />
    </span>
  );
}

export const Markdown: FC<
  PropsWithChildren<{
    className?: string;
    size?: MarkdownBodySize;
    /**
     * Opt-in: replace the model's status-glyph emoji (red circle, warning
     * triangle, green check, etc.) with colored icon-library icons. Default
     * OFF so shared consumers (canvas, Preview, comments) are unaffected;
     * only the Ask Puntt chat call sites opt in.
     */
    iconizeEmoji?: boolean;
  }>
> = ({ children, className, size = "3", iconizeEmoji = false }) => (
  <ReactMarkdown
    remarkPlugins={[remarkGfm]}
    rehypePlugins={[
      rehypeRaw,
      rehypeColorChip,
      rehypeSuperscript,
      ...(iconizeEmoji ? [rehypeIconizeEmoji] : []),
    ]}
    className={cx(
      // `break-words` (overflow-wrap: break-word) breaks genuinely
      // unbreakable strings like long URLs as a last resort WITHOUT
      // reducing min-content intrinsic size. The legacy
      // `word-break: break-word` we used before behaves like
      // `overflow-wrap: anywhere`, which DOES count intra-word break
      // points in min-content sizing — that collapsed auto-layout
      // table columns to ~one character and broke ordinary words
      // mid-character ("Regulator" -> "Regulat"/"or"). Since these
      // properties inherit, fixing the container covers tables, the
      // canvas, and every other nested markdown surface.
      "whitespace-normal break-words [&_p:empty]:hidden [&_p:last-child]:mb-0 [&_p]:mb-2",
      className,
    )}
    components={
      {
        h1: ({ node, ...props }) => (
          <Heading
            as="h1"
            size="6"
            className={headingClass}
            {...props}
            color={undefined}
          />
        ),
        h2: ({ node, ...props }) => (
          <Heading
            as="h2"
            size="5"
            className={headingClass}
            {...props}
            color={undefined}
          />
        ),
        h3: ({ node, ...props }) => (
          <Heading
            as="h3"
            size="4"
            className={headingClass}
            {...props}
            color={undefined}
          />
        ),
        h4: ({ node, ...props }) => (
          <Heading
            as="h4"
            size="3"
            className={headingClass}
            {...props}
            color={undefined}
          />
        ),
        h5: ({ node, ...props }) => (
          <Heading
            as="h5"
            size="2"
            className={headingClass}
            {...props}
            color={undefined}
          />
        ),
        h6: ({ node, ...props }) => (
          <Heading
            as="h6"
            size="1"
            className={headingClass}
            {...props}
            color={undefined}
          />
        ),
        strong: ({ node, ...props }) => (
          <Text as="span" weight="bold" {...props} color={undefined} />
        ),
        em: ({ node, ...props }) => (
          <Text as="span" className="italic" {...props} color={undefined} />
        ),
        //@ts-expect-error TS2322: different default prop types, but this is fully compatible
        del: ({ node, ...props }) => <Text {...props} />,
        p: ({ node, children, ...props }) => {
          if (isWhitespaceOnly(children)) return null;
          return (
            <Text as="p" size={size} {...props} color={undefined}>
              {children}
            </Text>
          );
        },
        li: ({ node, ...props }) => (
          <li>
            <Text as="span" size={size} {...props} color={undefined} />
          </li>
        ),
        ul: ({ node, ...props }) => (
          <ul
            className={cx(
              "list-disc whitespace-normal pl-4",
              LIST_TEXT_CLASS[size],
            )}
            {...props}
          />
        ),
        ol: ({ node, ...props }) => (
          <ol
            className={cx(
              "list-decimal whitespace-normal pl-5",
              LIST_TEXT_CLASS[size],
            )}
            {...props}
          />
        ),
        a: ({ node, ...props }) => (
          // eslint-disable-next-line jsx-a11y/anchor-has-content
          <a className="puntt-link underline" target="_blank" {...props} />
        ),
        pre: ({ node, ...props }) => (
          <pre className="max-w-56 overflow-auto" {...props} />
        ),
        code: ({ node, ...props }) => (
          <Text size={size} {...props} color={undefined} />
        ),
        hr: ({ node, ...props }) => (
          <hr className="my-4 border-carbon-300" {...props} />
        ),
        blockquote: ({ node, ...props }) => (
          <blockquote
            className="whitespace-wrap rounded-r-lg border-l-2 border-l-puntt-neutral-gray-6 bg-puntt-neutral-gray-2 py-3 pl-6"
            {...props}
          />
        ),
        table: ({ node, ...props }) => (
          <div className={tableWrapperClass}>
            <table
              className="w-full border-collapse text-left text-sm"
              {...props}
            />
          </div>
        ),
        thead: ({ node, ...props }) => (
          <thead className="bg-puntt-neutral-gray-3" {...props} />
        ),
        tbody: ({ node, ...props }) => <tbody {...props} />,
        tr: ({ node, ...props }) => (
          <tr
            className="border-b border-puntt-neutral-gray-6 last:border-b-0"
            {...props}
          />
        ),
        th: ({ node, ...props }) => (
          <th
            className="px-3 py-2 font-semibold text-puntt-neutral-gray-12"
            {...props}
          />
        ),
        td: ({ node, ...props }) => (
          <td
            className="px-3 py-2 align-top text-puntt-neutral-gray-12"
            {...props}
          />
        ),
        // Custom element emitted by `rehype-iconize-emoji` (opt-in only). The
        // tag never appears unless `iconizeEmoji` is set, so registering it
        // unconditionally is harmless for the default consumers.
        [EMOJI_ICON_TAG]: EmojiStatusIcon,
      } as Components
    }
  >
    {normalizeNewlines(children as string)}
  </ReactMarkdown>
);
